import { useEffect, useMemo, useState } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- loaders are intentionally keyed by filters and tenant */
import AdminLayout from '../../components/layout/AdminLayout';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';

type CampanaTipo = 'diaria_hora' | 'inactividad_dias';

type Campana = {
  id: string;
  gimnasio_id: string;
  nombre: string;
  tipo: CampanaTipo;
  mensaje_titulo: string;
  mensaje_cuerpo: string;
  hora_envio: string | null;
  dias_inactividad: number | null;
  activo: boolean;
  created_at: string;
};

type Envio = {
  id: string;
  created_at: string;
  fecha_referencia: string;
  estado: string;
  push_estado?: string | null;
  push_error?: string | null;
  campana: { nombre: string } | { nombre: string }[] | null;
  usuario: { nombre: string; email: string } | { nombre: string; email: string }[] | null;
};

export default function NotificacionesPage() {
  const { sucursalActiva } = useSucursal();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningCampaignId, setRunningCampaignId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [items, setItems] = useState<Campana[]>([]);
  const [envios, setEnvios] = useState<Envio[]>([]);
  const [enviosCount, setEnviosCount] = useState(0);
  const [enviosPage, setEnviosPage] = useState(1);
  const ENVIO_PAGE_SIZE = 20;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState({
    nombre: '',
    mensaje_titulo: '',
    mensaje_cuerpo: '',
    hora_envio: '08:00',
    dias_inactividad: 3,
  });

  const [form, setForm] = useState({
    nombre: '',
    tipo: 'diaria_hora' as CampanaTipo,
    mensaje_titulo: '¡Vamos con todo hoy!',
    mensaje_cuerpo: 'Recuerda entrenar y mantener tu constancia.',
    hora_envio: '08:00',
    dias_inactividad: 3,
  });

  const gymId = sucursalActiva?.gimnasio_id || null;

  const getValidAccessToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session?.expires_at ?? 0;
    const isExpiredOrNearExpiry = !session?.access_token || expiresAt <= now + 60;

    if (isExpiredOrNearExpiry) {
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr || !refreshed?.session?.access_token) {
        throw new Error('Tu sesión expiró. Vuelve a iniciar sesión.');
      }
      return refreshed.session.access_token;
    }

    return session.access_token;
  };

  const invokePushDispatch = async (targetGymId: string) => {
    const accessToken = await getValidAccessToken();
    const { data, error } = await supabase.functions.invoke('notificaciones-dispatch', {
      body: { gimnasio_id: targetGymId },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (error) {
      throw error;
    }
    return data as {
      ok?: boolean;
      push?: { enabled?: boolean; sent?: number; failed?: number; error?: string };
    } | null;
  };

  const canSave = useMemo(() => {
    if (!gymId) return false;
    if (!form.nombre.trim() || !form.mensaje_titulo.trim() || !form.mensaje_cuerpo.trim()) return false;
    if (form.tipo === 'diaria_hora') return !!form.hora_envio;
    if (form.tipo === 'inactividad_dias') return Number(form.dias_inactividad) > 0;
    return false;
  }, [form, gymId]);

  const loadCampanas = async () => {
    if (!gymId) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { data, error: qErr } = await supabase
        .from('notificaciones_campanas')
        .select('id,gimnasio_id,nombre,tipo,mensaje_titulo,mensaje_cuerpo,hora_envio,dias_inactividad,activo,created_at')
        .eq('gimnasio_id', gymId)
        .order('created_at', { ascending: false });
      if (qErr) throw qErr;
      setItems((data || []) as Campana[]);
    } catch (e: any) {
      setError(e?.message || 'No se pudieron cargar campañas.');
    } finally {
      setLoading(false);
    }
  };

  const loadEnvios = async () => {
    if (!gymId) {
      setEnvios([]);
      setEnviosCount(0);
      return;
    }
    try {
      const from = (enviosPage - 1) * ENVIO_PAGE_SIZE;
      const to = from + ENVIO_PAGE_SIZE - 1;
      const { data, count, error: qErr } = await supabase
        .from('notificaciones_envios')
        .select(`
          id,
          created_at,
          fecha_referencia,
          estado,
          push_estado,
          push_error,
          campana:notificaciones_campanas(nombre),
          usuario:usuarios(nombre,email)
        `, { count: 'exact' })
        .eq('gimnasio_id', gymId)
        .or('estado.neq.enviado,push_estado.in.(error,parcial,sin_dispositivo)')
        .order('created_at', { ascending: false })
        .range(from, to);
      if (qErr) throw qErr;
      setEnvios((data || []) as Envio[]);
      setEnviosCount(Number(count || 0));
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar historial de envíos.');
    }
  };

  useEffect(() => {
    void loadCampanas();
  }, [gymId]);

  useEffect(() => {
    void loadEnvios();
  }, [gymId, enviosPage]);

  const createCampana = async () => {
    if (!canSave || !gymId) return;
    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      const payload: any = {
        gimnasio_id: gymId,
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        mensaje_titulo: form.mensaje_titulo.trim(),
        mensaje_cuerpo: form.mensaje_cuerpo.trim(),
        hora_envio: form.tipo === 'diaria_hora' ? form.hora_envio : null,
        dias_inactividad: form.tipo === 'inactividad_dias' ? Number(form.dias_inactividad) : null,
      };

      const { error: insErr } = await supabase.from('notificaciones_campanas').insert(payload);
      if (insErr) throw insErr;

      setNotice('Campaña creada correctamente.');
      setForm((p) => ({ ...p, nombre: '' }));
      await loadCampanas();
    } catch (e: any) {
      setError(e?.message || 'No se pudo crear la campaña.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (row: Campana) => {
    try {
      const { error: upErr } = await supabase
        .from('notificaciones_campanas')
        .update({ activo: !row.activo })
        .eq('id', row.id);
      if (upErr) throw upErr;
      await loadCampanas();
    } catch (e: any) {
      setError(e?.message || 'No se pudo actualizar la campaña.');
    }
  };

  const runDispatchNow = async () => {
    if (!gymId) return;
    try {
      setRunning(true);
      setError(null);
      setNotice(null);
      const { data, error: rpcErr } = await supabase.rpc('dispatch_notificaciones_programadas', {
        p_gimnasio_id: gymId,
      });
      if (rpcErr) throw rpcErr;
      const sent = Number((data as any)?.sent || 0);
      const pushResult = await invokePushDispatch(gymId);
      const pushSent = Number(pushResult?.push?.sent || 0);
      const pushFailed = Number(pushResult?.push?.failed || 0);
      const pushError = String(pushResult?.push?.error || '').trim();
      setNotice(
        `Despacho ejecutado. Notificaciones generadas: ${sent}. Push enviados: ${pushSent}. Fallidos: ${pushFailed}.${pushError ? ` Error: ${pushError}` : ''}`,
      );
      setEnviosPage(1);
      await loadEnvios();
    } catch (e: any) {
      setError(e?.message || 'No se pudo ejecutar el despacho.');
    } finally {
      setRunning(false);
    }
  };

  const runCampaignNow = async (row: Campana) => {
    try {
      setRunningCampaignId(row.id);
      setError(null);
      setNotice(null);
      const { data, error: rpcErr } = await supabase.rpc('dispatch_notificacion_campana', {
        p_campana_id: row.id,
        p_forzar: true,
      });
      if (rpcErr) throw rpcErr;
      const sent = Number((data as any)?.sent || 0);
      const skipped = Number((data as any)?.skipped || 0);
      const pushResult = gymId ? await invokePushDispatch(gymId) : null;
      const pushSent = Number(pushResult?.push?.sent || 0);
      const pushFailed = Number(pushResult?.push?.failed || 0);
      const pushError = String(pushResult?.push?.error || '').trim();
      setNotice(
        `Campaña "${row.nombre}" ejecutada. Generadas: ${sent}. Omitidas: ${skipped}. Push enviados: ${pushSent}. Fallidos: ${pushFailed}.${pushError ? ` Error: ${pushError}` : ''}`,
      );
      setEnviosPage(1);
      await loadEnvios();
    } catch (e: any) {
      setError(e?.message || 'No se pudo forzar la campaña.');
    } finally {
      setRunningCampaignId(null);
    }
  };

  const startEdit = (row: Campana) => {
    setEditingId(row.id);
    setEditingForm({
      nombre: row.nombre,
      mensaje_titulo: row.mensaje_titulo,
      mensaje_cuerpo: row.mensaje_cuerpo,
      hora_envio: String(row.hora_envio || '08:00').slice(0, 5),
      dias_inactividad: row.dias_inactividad || 3,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingForm({
      nombre: '',
      mensaje_titulo: '',
      mensaje_cuerpo: '',
      hora_envio: '08:00',
      dias_inactividad: 3,
    });
  };

  const saveEdit = async (row: Campana) => {
    try {
      setError(null);
      const payload = {
        nombre: editingForm.nombre.trim(),
        mensaje_titulo: editingForm.mensaje_titulo.trim(),
        mensaje_cuerpo: editingForm.mensaje_cuerpo.trim(),
        hora_envio: row.tipo === 'diaria_hora' ? editingForm.hora_envio : null,
        dias_inactividad: row.tipo === 'inactividad_dias' ? Number(editingForm.dias_inactividad || 0) : null,
      };
      const { error: upErr } = await supabase.from('notificaciones_campanas').update(payload).eq('id', row.id);
      if (upErr) throw upErr;
      setNotice('Campaña actualizada.');
      cancelEdit();
      await loadCampanas();
    } catch (e: any) {
      setError(e?.message || 'No se pudo actualizar la campaña.');
    }
  };

  const deleteCampana = async (row: Campana) => {
    const ok = window.confirm(`¿Eliminar campaña "${row.nombre}"? Esta acción no se puede deshacer.`);
    if (!ok) return;
    try {
      setError(null);
      const { error: delErr } = await supabase.from('notificaciones_campanas').delete().eq('id', row.id);
      if (delErr) throw delErr;
      setNotice('Campaña eliminada.');
      await loadCampanas();
      setEnviosPage(1);
      await loadEnvios();
    } catch (e: any) {
      setError(e?.message || 'No se pudo eliminar la campaña.');
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Notificaciones</h1>
            <p className="text-gray-600 mt-1">Campañas automáticas para usuarios de la app móvil.</p>
          </div>
          <button
            onClick={runDispatchNow}
            disabled={!gymId || running}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {running ? 'Ejecutando...' : 'Ejecutar ahora'}
          </button>
        </div>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">{error}</div>}
        {notice && <div className="rounded-lg border border-green-200 bg-green-50 text-green-700 px-4 py-3">{notice}</div>}

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Nueva campaña</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                value={form.nombre}
                onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Motivación mañanera"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={form.tipo}
                onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value as CampanaTipo }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="diaria_hora">Diaria por hora</option>
                <option value="inactividad_dias">Inactividad por días</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {form.tipo === 'diaria_hora' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora de envío</label>
                <input
                  type="time"
                  value={form.hora_envio}
                  onChange={(e) => setForm((p) => ({ ...p, hora_envio: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Días sin entrenar</label>
                <input
                  type="number"
                  min={1}
                  value={form.dias_inactividad}
                  onChange={(e) => setForm((p) => ({ ...p, dias_inactividad: Number(e.target.value || 0) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
            <input
              value={form.mensaje_titulo}
              onChange={(e) => setForm((p) => ({ ...p, mensaje_titulo: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje</label>
            <textarea
              value={form.mensaje_cuerpo}
              onChange={(e) => setForm((p) => ({ ...p, mensaje_cuerpo: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[96px]"
            />
          </div>

          <button
            onClick={createCampana}
            disabled={!canSave || saving}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Crear campaña'}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Campañas activas</h2>
          </div>
          {loading ? (
            <div className="p-6 text-gray-500">Cargando campañas...</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-gray-500">No hay campañas configuradas.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {items.map((row) => (
                <div key={row.id} className="p-6 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {editingId === row.id ? (
                      <div className="space-y-3">
                        <input
                          value={editingForm.nombre}
                          onChange={(e) => setEditingForm((p) => ({ ...p, nombre: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                        <input
                          value={editingForm.mensaje_titulo}
                          onChange={(e) => setEditingForm((p) => ({ ...p, mensaje_titulo: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                        <textarea
                          value={editingForm.mensaje_cuerpo}
                          onChange={(e) => setEditingForm((p) => ({ ...p, mensaje_cuerpo: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[84px]"
                        />
                        {row.tipo === 'diaria_hora' ? (
                          <input
                            type="time"
                            value={editingForm.hora_envio}
                            onChange={(e) => setEditingForm((p) => ({ ...p, hora_envio: e.target.value }))}
                            className="w-full md:w-48 border border-gray-300 rounded-lg px-3 py-2"
                          />
                        ) : (
                          <input
                            type="number"
                            min={1}
                            value={editingForm.dias_inactividad}
                            onChange={(e) => setEditingForm((p) => ({ ...p, dias_inactividad: Number(e.target.value || 0) }))}
                            className="w-full md:w-48 border border-gray-300 rounded-lg px-3 py-2"
                          />
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="font-semibold text-gray-900">{row.nombre}</p>
                        <p className="text-sm text-gray-600 mt-1">{row.mensaje_titulo}</p>
                        <p className="text-sm text-gray-500 mt-1">{row.mensaje_cuerpo}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {row.tipo === 'diaria_hora'
                            ? `Regla: todos los días a las ${String(row.hora_envio || '--:--').slice(0, 5)}`
                            : `Regla: inactividad de ${row.dias_inactividad || 0} días`}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => toggleActivo(row)}
                      className={`px-3 py-2 rounded-lg text-sm ${row.activo ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}
                    >
                      {row.activo ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      onClick={() => runCampaignNow(row)}
                      disabled={runningCampaignId === row.id}
                      className="px-3 py-2 rounded-lg text-sm bg-indigo-100 text-indigo-800 disabled:opacity-50"
                    >
                      {runningCampaignId === row.id ? 'Forzando...' : 'Forzar ahora'}
                    </button>
                    {editingId === row.id ? (
                      <>
                        <button onClick={() => saveEdit(row)} className="px-3 py-2 rounded-lg text-sm bg-emerald-600 text-white">
                          Guardar
                        </button>
                        <button onClick={cancelEdit} className="px-3 py-2 rounded-lg text-sm bg-gray-100 text-gray-700">
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(row)} className="px-3 py-2 rounded-lg text-sm bg-blue-100 text-blue-800">
                          Editar
                        </button>
                        <button onClick={() => deleteCampana(row)} className="px-3 py-2 rounded-lg text-sm bg-red-100 text-red-800">
                          Eliminar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Historial de fallos</h2>
            <button onClick={loadEnvios} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm">
              Recargar
            </button>
          </div>
          {envios.length === 0 ? (
            <div className="p-6 text-gray-500">Sin envíos fallidos.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3">Fecha</th>
                    <th className="text-left px-4 py-3">Campaña</th>
                    <th className="text-left px-4 py-3">Usuario</th>
                    <th className="text-left px-4 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {envios.map((row) => {
                    const c = Array.isArray(row.campana) ? row.campana[0] : row.campana;
                    const u = Array.isArray(row.usuario) ? row.usuario[0] : row.usuario;
                    return (
                      <tr key={row.id}>
                        <td className="px-4 py-3 text-gray-600">
                          {new Date(row.created_at).toLocaleString('es-CL')}
                        </td>
                        <td className="px-4 py-3 text-gray-900">{c?.nombre || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{u ? `${u.nombre} (${u.email})` : '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{row.push_estado || row.estado}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Total fallos: {enviosCount}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm disabled:opacity-50"
                disabled={enviosPage <= 1}
                onClick={() => setEnviosPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700">
                Página {enviosPage} de {Math.max(1, Math.ceil(enviosCount / ENVIO_PAGE_SIZE))}
              </span>
              <button
                className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm disabled:opacity-50"
                disabled={enviosPage >= Math.max(1, Math.ceil(enviosCount / ENVIO_PAGE_SIZE))}
                onClick={() => setEnviosPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
